import { useState, useMemo } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  getYear,
  getMonth,
  setMonth,
  setYear,
} from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarIcon } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { themed } from './themed';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Button } from './Button';
import { NativeSelect } from './NativeSelect';

export interface DatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  error?: boolean;
  /** Optional classes for the trigger button (e.g. compact popover variant). */
  triggerClassName?: string;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const currentYear = new Date().getFullYear();
const MIN_YEAR = currentYear - 120;
const YEARS = Array.from({ length: currentYear - MIN_YEAR + 1 }, (_, i) => currentYear - i);

export function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  id,
  error,
  triggerClassName,
}: DatePickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? new Date() : d;
    }
    return new Date();
  });

  const selectedDate = useMemo(() => {
    if (!value) return undefined;
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  const viewYear = getYear(viewDate);
  const viewMonth = getMonth(viewDate);

  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: calStart, end: calEnd });

  function selectDay(day: Date) {
    onChange(format(day, 'yyyy-MM-dd'));
    setOpen(false);
  }

  function handleMonthChange(m: string) {
    setViewDate((d) => setMonth(d, parseInt(m, 10)));
  }

  function handleYearChange(y: string) {
    setViewDate((d) => setYear(d, parseInt(y, 10)));
  }

  const displayValue = value
    ? format(new Date(value), 'dd MMM yyyy')
    : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          className={cn(
            themed.field.base,
            themed.field.border,
            themed.focusRing,
            'flex items-center gap-2 text-left',
            error && 'border-coral-500 focus-visible:ring-coral-500',
            !value && themed.text.secondary,
            triggerClassName,
          )}
        >
          <CalendarIcon className="size-3.5 flex-none opacity-50" />
          <span className="flex-1 truncate">{value ? displayValue : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="flex items-center gap-1 mb-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 flex-none"
            onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <NativeSelect
            value={String(viewMonth)}
            onChange={(e) => handleMonthChange(e.target.value)}
            className="h-8 w-[110px] text-xs font-medium border-none shadow-none focus-visible:ring-0 cursor-pointer px-1 py-0 bg-transparent"
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i}>{m}</option>
            ))}
          </NativeSelect>
          <NativeSelect
            value={String(viewYear)}
            onChange={(e) => handleYearChange(e.target.value)}
            className="h-8 w-[72px] text-xs font-medium border-none shadow-none focus-visible:ring-0 cursor-pointer px-1 py-0 bg-transparent"
          >
            {YEARS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </NativeSelect>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 flex-none"
            onClick={() => setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-0.5 text-center">
          {DAY_NAMES.map((d) => (
            <div key={d} className="text-[11px] font-medium text-[var(--text-secondary)] w-8 h-7 flex items-center justify-center">
              {d}
            </div>
          ))}
          {days.map((day) => {
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const isCurrentMonth = isSameMonth(day, viewDate);
            const today = isToday(day);

            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => selectDay(day)}
                className={cn(
                  'w-8 h-8 text-sm rounded-full flex items-center justify-center transition-colors',
                  isCurrentMonth ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)] opacity-40',
                  isSelected && 'bg-brand-500 text-white',
                  !isSelected && today && 'border border-brand-300 text-brand-600 font-semibold',
                  !isSelected && isCurrentMonth && 'hover:bg-[var(--bg-surface-muted)]',
                )}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
        {value && (
          <div className="mt-2 pt-2 border-t border-[var(--border-subtle)]">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
